import React, { useState } from 'react'
import { cn } from '@/utils'
import { Eye, EyeOff } from 'lucide-react'
import { TextInput } from './text'

interface PasswordStrength {
  score: number
  message: string
  color: string
}

interface Props extends Omit<React.ComponentProps<typeof TextInput>, 'type'> {
  showStrengthIndicator?: boolean
}

const getPasswordStrength = (password: string): PasswordStrength => {
  const hasLowerCase = /[a-z]/.test(password)
  const hasUpperCase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
  const isLongEnough = password.length >= 8

  const score = [
    hasLowerCase,
    hasUpperCase,
    hasNumber,
    hasSpecialChar,
    isLongEnough,
  ].filter(Boolean).length

  // *@INFO: Define strength levels
  const strengthLevels: Record<number, PasswordStrength> = {
    0: { score: 0, message: 'Very Weak', color: 'bg-red-error' },
    1: { score: 1, message: 'Weak', color: 'bg-orange-500' },
    2: { score: 2, message: 'Fair', color: 'bg-yellow-500' },
    3: { score: 3, message: 'Good', color: 'bg-blue-500' },
    4: { score: 4, message: 'Strong', color: 'bg-green-100' },
  }

  return strengthLevels[Math.min(4, score)] || strengthLevels[0]
}

export const PasswordInput = ({
  showStrengthIndicator = true,
  className,
  ...props
}: Props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [strength, setStrength] = useState<PasswordStrength>({
    score: 0,
    message: '',
    color: '',
  })

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (showStrengthIndicator) {
      setStrength(getPasswordStrength(e.target.value))
    }
    props.onChange?.(e)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const PasswordToggleIcon = () => (
    <button
      type="button"
      onClick={togglePasswordVisibility}
      className={cn(
        'p-1 hover:text-gray-700',
        props.disabled ? 'cursor-not-allowed text-gray-400' : 'text-gray-500'
      )}
      disabled={props.disabled}
    >
      {showPassword ? (
        <EyeOff className="h-4 w-4" />
      ) : (
        <Eye className="h-4 w-4" />
      )}
    </button>
  )

  return (
    <div className="flex flex-col gap-1">
      <TextInput
        {...props}
        type={showPassword ? 'text' : 'password'}
        className={className}
        rightIcon={<PasswordToggleIcon />}
        displayIcon={true}
        onChange={handlePasswordChange}
      />
      {showStrengthIndicator && strength.message && !props.disabled && (
        <div className="mt-1">
          <div className="flex h-1 overflow-hidden rounded-full bg-gray-200">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className={cn(
                  'h-full w-1/5 transition-all duration-300',
                  index <= strength.score ? strength.color : 'bg-gray-200'
                )}
              />
            ))}
          </div>
          <span className={cn('mt-1 text-xs', `text-${strength.color.split('-')[1]}-500`)}>
            {strength.message}
          </span>
        </div>
      )}
    </div>
  )
}

PasswordInput.displayName = 'PasswordInput'